import axios from "axios";
import { getDeviceId, wipeOfflineData, secureSet, secureGet, secureDel } from "@/lib/secureStore";
import { cachePut, cacheGet } from "@/lib/offlineCache";
import { getHaatId } from "@/lib/haat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Secure session storage.
 * Tokens/user previously lived in plaintext localStorage (code-quality audit
 * finding). They now stay in memory and persist AES-256-GCM-encrypted in
 * IndexedDB via secureStore. `hydrateSession()` loads them before first render.
 */
const SESSION_SLOT = "am_session_v1";
const LEGACY_TOKEN_KEY = "am_token";
const LEGACY_USER_KEY = "am_user";
let _token = null;
let _user = null;

export const setSession = (token, user) => {
  _token = token;
  _user = user;
  secureSet(SESSION_SLOT, { token, user }).catch((e) => console.debug("session persist failed", e));
};

export const clearSession = () => {
  _token = null;
  _user = null;
  secureDel(SESSION_SLOT);
  localStorage.removeItem("am_haat_id");
};

/** Load the encrypted session into memory. Runs once before first render.
 *  Also migrates + removes any legacy plaintext localStorage session. */
export async function hydrateSession() {
  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  const legacyUser = localStorage.getItem(LEGACY_USER_KEY);
  if (legacyToken && legacyUser) {
    try { _user = JSON.parse(legacyUser); _token = legacyToken; } catch (e) { console.debug("legacy session parse failed", e); }
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
    if (_token && _user) {
      await secureSet(SESSION_SLOT, { token: _token, user: _user }).catch(() => {});
      return;
    }
  }
  const rec = await secureGet(SESSION_SLOT);
  if (rec?.token) { _token = rec.token; _user = rec.user; }
}

export const logoutRemote = async () => {
  try {
    await api.post("/auth/logout");
  } catch (e) { console.debug("logout call failed (session already cleared locally)", e); }
  // Also clear any draft PII on logout
  localStorage.removeItem("am_new_reg_draft");
  clearSession();
};

export const getToken = () => _token;
export const getUser = () => _user;

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

const cacheKeyFor = (cfg) => {
  const haat = cfg?.headers?.["X-Haat-Id"] || "";
  return (cfg?.url || "") + (cfg?.params ? `?${JSON.stringify(cfg.params)}` : "") + (haat ? `#haat:${haat}` : "");
};

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  cfg.headers["X-Device-Id"] = getDeviceId();
  // Multi-haat: scope subscriber requests to the selected Pashu Haat.
  const haat = getHaatId();
  if (haat) cfg.headers["X-Haat-Id"] = haat;
  return cfg;
});
api.interceptors.response.use(
  (r) => {
    // Persist every successful GET into the encrypted offline cache.
    if (r.config?.method === "get" && r.status === 200 && !r.fromCache) {
      cachePut(cacheKeyFor(r.config), r.data);
    }
    return r;
  },
  async (err) => {
    // Remote device revoke — wipe local data and force sign-out.
    if (err?.response?.data?.detail === "device_revoked") {
      await wipeOfflineData();
      clearSession();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login?revoked=1";
      }
      return Promise.reject(err);
    }
    // Offline read fallback — serve encrypted local cache for GETs.
    if (!err?.response && err?.config?.method === "get") {
      const cached = await cacheGet(cacheKeyFor(err.config));
      if (cached !== undefined) {
        return { data: cached, status: 200, statusText: "OK (offline cache)", headers: {}, config: err.config, fromCache: true };
      }
    }
    if (err?.response?.status === 401) {
      clearSession();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const roleHome = (role) => {
  if (role === "super_admin") return "/admin";
  if (role === "subscriber") return "/subscriber";
  if (role === "helper") return "/helper";
  if (role === "driver") return "/driver";
  if (role === "vet") return "/vet";
  return "/";
};
